import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { Phone, Email, LocationOn, Facebook, Twitter, Instagram, LinkedIn } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import logoWhite from "assets/icon-white-transparent.png";
import { SG, TEXT } from "theme/publicDarkTokens";

const FooterLink = ({ label, onClick }) => (
  <Box
    component="a"
    onClick={onClick}
    sx={{
      cursor: "pointer",
      display: "block",
      fontSize: 14,
      lineHeight: 2.15,
      color: "rgba(248,245,255,.42)",
      textDecoration: "none",
      transition: "color .2s",
      "&:hover": { color: "#A855F7" },
    }}
  >
    {label}
  </Box>
);

const SocialIcon = ({ children }) => (
  <Box
    component="a"
    href="#"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: "8px",
      background: "rgba(248,245,255,.06)",
      color: "rgba(248,245,255,.45)",
      textDecoration: "none",
      transition: "all .2s",
      "&:hover": { background: "rgba(124,58,237,.22)", color: "#A855F7" },
      "& svg": { fontSize: 18 },
    }}
  >
    {children}
  </Box>
);

const ContactRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        background: "rgba(124,58,237,.1)",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: "1px",
        "& svg": { fontSize: 16, color: "#A855F7" },
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          color: "rgba(248,245,255,.28)",
          mb: "3px",
          letterSpacing: ".04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.75)", lineHeight: 1.68 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const ColumnHeading = ({ children }) => (
  <Typography
    component="h4"
    sx={{
      fontFamily: SG,
      fontWeight: 700,
      fontSize: 11,
      color: "rgba(248,245,255,.3)",
      letterSpacing: ".1em",
      textTransform: "uppercase",
      mb: 2.5,
    }}
  >
    {children}
  </Typography>
);

const PublicFooterDark = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const quickLinks = [
    { label: t("navigation.trackPackage"), path: "/track" },
    { label: t("navigation.calculator"), path: "/calculator" },
    { label: t("navigation.services"), path: "/services" },
    { label: t("navigation.about"), path: "/about" },
    { label: t("navigation.contact"), path: "/contact" },
    { label: t("navigation.login"), path: "/login" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: "#03020D",
        pt: 9,
        pb: 4.5,
        borderTop: "1px solid rgba(248,245,255,.05)",
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 2.5, md: 5 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.6fr 1fr 1.2fr" },
            gap: { xs: 5, md: 9 },
            mb: 7,
          }}
        >
          {/* Brand */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.25 }}>
              <Box
                component="img"
                src={logoWhite}
                alt="Winner Express"
                sx={{ width: 28, height: 28, objectFit: "contain" }}
              />
              <Typography
                sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, letterSpacing: "-.02em" }}
              >
                {t("home.title")}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                color: "rgba(248,245,255,.36)",
                lineHeight: 1.88,
                mb: 3.5,
                maxWidth: 260,
              }}
            >
              {t("footer.description")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <SocialIcon><Facebook /></SocialIcon>
              <SocialIcon><Twitter /></SocialIcon>
              <SocialIcon><Instagram /></SocialIcon>
              <SocialIcon><LinkedIn /></SocialIcon>
            </Box>
          </Box>

          {/* Quick links */}
          <Box>
            <ColumnHeading>{t("footer.quickLinks")}</ColumnHeading>
            {quickLinks.map((link) => (
              <FooterLink key={link.path} label={link.label} onClick={() => navigate(link.path)} />
            ))}
          </Box>

          {/* Contact */}
          <Box>
            <ColumnHeading>{t("footer.contactInfo")}</ColumnHeading>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
              <ContactRow icon={<Phone />} label={t("contact.info.phone")} value={t("contact.info.phoneNumber")} />
              <ContactRow icon={<Email />} label={t("contact.info.email")} value={t("contact.info.emailAddress")} />
              <ContactRow icon={<LocationOn />} label={t("contact.info.office")} value={t("contact.info.addressText")} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid rgba(248,245,255,.05)", pt: 3.5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.2)" }}>
            {t("footer.copyright")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicFooterDark;
